// src/lib/intelligent-proposal-integration.ts
// FIXED VERSION: Efficient database loading without hanging

import { supabase } from './supabase';

export interface IntelligentProposalContext {
  // Raw document content for OpenAI analysis
  raw_document_content: string;
  profile_data: any;
  manual_personnel: any[];
  manual_past_performance: any[];
  manual_capabilities: any[];
  // Pricing data for cost proposals
  pricing_data: any[];
  // Document text combined from uploads
  documents_text: string;

  // Metadata about data sources
  generation_context: {
    data_quality_score: number;
    completeness_score: number;
    confidence_level: 'high' | 'medium' | 'low';
    data_sources_available: string[];
    documents_count: number;
    total_document_text_length: number;
  };
}

export class IntelligentProposalGenerator {
  
  static async generateIntelligentProposal(
    profileId: string,
    contractData: any
  ): Promise<{
    intelligentContext: IntelligentProposalContext;
    proposalInstructions: string;
    dataQualityReport: any;
  }> {

    console.log('🧠 Starting FIXED intelligent proposal generation...');
    console.log(`📊 Profile ID: ${profileId}`);
    console.log(`📋 Contract: ${contractData.title}`);

    try {
      // 1. Load profile data with timeouts to prevent hanging
      const profileData = await this.loadCompleteProfileDataFixed(profileId);

      // 2. Combine document text efficiently
      let combinedDocumentText = this.combineAllDocumentTextFixed(profileData.documents);

      // 3. CRITICAL: Include contract-specific document text if available
      // This comes from user-uploaded documents in contract_documents table
      if (contractData.documentText && contractData.documentText.length > 0) {
        console.log(`📄 Adding contract document text: ${contractData.documentText.length} characters`);
        combinedDocumentText = `=== CONTRACT-SPECIFIC DOCUMENTS (SOLICITATION/RFP) ===\n${contractData.documentText}\n\n${combinedDocumentText}`;
      }
      
      // 3. Calculate data quality metrics
      const dataQualityMetrics = this.calculateDataQualityFixed(profileData);
      
      // 4. Create intelligent context with available data
      const intelligentContext: IntelligentProposalContext = {
        raw_document_content: combinedDocumentText,
        profile_data: profileData.profile || {},
        manual_personnel: profileData.keyPersonnel || [],
        manual_past_performance: profileData.pastPerformance || [],
        manual_capabilities: profileData.capabilities || [],
        pricing_data: profileData.pricingData || [],
        documents_text: combinedDocumentText,
        generation_context: dataQualityMetrics
      };
      
      // 5. Generate AI instructions
      const proposalInstructions = this.generateCleanAIInstructions(
        intelligentContext,
        contractData
      );
      
      // 6. Generate data quality report
      const dataQualityReport = this.generateCleanDataQualityReport(
        intelligentContext,
        profileData
      );
      
      console.log('✅ FIXED intelligence generation completed');
      console.log(`📄 Document text length: ${combinedDocumentText.length} characters`);
      console.log(`📊 Data quality score: ${dataQualityMetrics.data_quality_score}/100`);
      
      return {
        intelligentContext,
        proposalInstructions,
        dataQualityReport
      };
      
    } catch (error) {
      console.error('❌ Error in fixed intelligent proposal generation:', error);
      throw error;
    }
  }

  // FIXED: Load profile data with proper timeout handling
  private static async loadCompleteProfileDataFixed(profileId: string): Promise<any> {
    console.log('📚 Loading complete profile data with timeout protection...');
    
    try {
      // Load data sequentially with individual timeouts to prevent hanging
      let profile = null;
      let keyPersonnel: any[] = [];
      let pastPerformance: any[] = [];
      let documents: any[] = [];
      let capabilities: any[] = [];
      let pricingData: any[] = [];

      // 1. Load profile (essential)
      try {
        console.log('Loading profile...');
        const { data: profileData, error: profileError } = await supabase
          .from('profiles')
          .select('*')
          .eq('id', profileId)
          .single()
          .abortSignal(AbortSignal.timeout(5000));
        
        if (profileError && profileError.code !== 'PGRST116') {
          console.error('Profile loading error:', profileError);
        } else {
          profile = profileData;
          console.log('✅ Profile loaded');
        }
      } catch (error) {
        console.warn('⚠️ Profile loading timeout/error:', error);
      }

      // 2. Load personnel (important but not critical)
      try {
        console.log('Loading personnel...');
        const { data: personnelData, error: personnelError } = await supabase
          .from('key_personnel')
          .select('*')
          .eq('profile_id', profileId)
          .abortSignal(AbortSignal.timeout(3000));
        
        if (!personnelError && personnelData) {
          keyPersonnel = personnelData;
          console.log(`✅ Personnel loaded: ${keyPersonnel.length} records`);
        }
      } catch (error) {
        console.warn('⚠️ Personnel loading timeout/error:', error);
      }

      // 3. Load past performance (important but not critical)
      try {
        console.log('Loading past performance...');
        const { data: pastPerfData, error: pastPerfError } = await supabase
          .from('past_performance')
          .select('*')
          .eq('profile_id', profileId)
          .abortSignal(AbortSignal.timeout(3000));
        
        if (!pastPerfError && pastPerfData) {
          pastPerformance = pastPerfData;
          console.log(`✅ Past performance loaded: ${pastPerformance.length} records`);
        }
      } catch (error) {
        console.warn('⚠️ Past performance loading timeout/error:', error);
      }

      // 4. Load documents (critical for intelligence)
      try {
        console.log('Loading documents...');
        const { data: docsData, error: docsError } = await supabase
          .from('profile_documents')
          .select('id, file_name, document_type, extracted_text, metadata')
          .eq('profile_id', profileId)
          .abortSignal(AbortSignal.timeout(5000));
        
        if (!docsError && docsData) {
          documents = docsData;
          console.log(`✅ Documents loaded: ${documents.length} records`);
        }
      } catch (error) {
        console.warn('⚠️ Documents loading timeout/error:', error);
      }

      // 5. Load capabilities (important but not critical)
      try {
        console.log('Loading capabilities...');
        const { data: capabilitiesData, error: capabilitiesError } = await supabase
          .from('capabilities')
          .select('*')
          .eq('profile_id', profileId)
          .abortSignal(AbortSignal.timeout(3000));
        
        if (!capabilitiesError && capabilitiesData) {
          capabilities = capabilitiesData;
          console.log(`✅ Capabilities loaded: ${capabilities.length} records`);
        }
      } catch (error) {
        console.warn('⚠️ Capabilities loading timeout/error:', error);
      }

      // 6. Load pricing data (nice to have)
      try {
        console.log('Loading pricing data...');
        const { data: pricingDataResult, error: pricingError } = await supabase
          .from('enhanced_pricing_data')
          .select('*')
          .eq('profile_id', profileId)
          .limit(50) // Limit to prevent large loads
          .abortSignal(AbortSignal.timeout(2000));
        
        if (!pricingError && pricingDataResult) {
          pricingData = pricingDataResult;
          console.log(`✅ Pricing data loaded: ${pricingData.length} records`);
        }
      } catch (error) {
        console.warn('⚠️ Pricing data loading timeout/error:', error);
      }

      console.log('📄 Data loading summary:');
      console.log(`  - Profile: ${profile ? 'Loaded' : 'Failed'}`);
      console.log(`  - Personnel: ${keyPersonnel.length} records`);
      console.log(`  - Past Performance: ${pastPerformance.length} records`);
      console.log(`  - Documents: ${documents.length} records`);
      console.log(`  - Capabilities: ${capabilities.length} records`);
      console.log(`  - Pricing Data: ${pricingData.length} records`);

      return {
        profile: profile || { company_name: 'Professional Services Company' },
        keyPersonnel,
        pastPerformance,
        documents,
        capabilities,
        pricingData
      };
      
    } catch (error) {
      console.error('❌ Error in loadCompleteProfileDataFixed:', error);
      
      // Return minimal fallback data to prevent system failure
      return {
        profile: { company_name: 'Professional Services Company' },
        keyPersonnel: [],
        pastPerformance: [],
        documents: [],
        capabilities: [],
        pricingData: []
      };
    }
  }

  // FIXED: Combine document text efficiently
  private static combineAllDocumentTextFixed(documents: any[]): string {
    if (!documents || documents.length === 0) {
      return 'No document content available for analysis.';
    }
    
    console.log(`📄 Combining text from ${documents.length} documents...`);
    
    const validDocuments = documents.filter(doc => 
      doc.extracted_text && 
      doc.extracted_text.length > 0 && 
      doc.extracted_text !== 'undefined' &&
      doc.extracted_text !== 'null'
    );
    
    if (validDocuments.length === 0) {
      return 'No valid document content available for analysis.';
    }
    
    const combinedText = validDocuments
      .map(doc => {
        const fileName = doc.file_name || 'Unknown Document';
        const content = doc.extracted_text || '';
        return `=== ${fileName} ===\n${content.slice(0, 10000)}`; // Limit each doc to 10k chars
      })
      .join('\n\n');
    
    console.log(`✅ Combined text from ${validDocuments.length} documents (${combinedText.length} characters)`);
    
    return combinedText;
  }

  // FIXED: Calculate data quality efficiently
  private static calculateDataQualityFixed(profileData: any): any {
    const documentsWithText = (profileData.documents || []).filter(d => 
      d.extracted_text && 
      d.extracted_text.length > 0 &&
      d.extracted_text !== 'undefined' &&
      d.extracted_text !== 'null'
    );
    
    const totalTextLength = documentsWithText.reduce((sum, doc) => 
      sum + (doc.extracted_text?.length || 0), 0
    );
    
    // Calculate quality score based on available data
    let qualityScore = 0;
    if (profileData.profile?.company_name) qualityScore += 20;
    if ((profileData.keyPersonnel || []).length > 0) qualityScore += 20;
    if ((profileData.pastPerformance || []).length > 0) qualityScore += 20;
    if (documentsWithText.length > 0) qualityScore += 20;
    if ((profileData.capabilities || []).length > 0) qualityScore += 20;
    
    const confidenceLevel = qualityScore > 60 ? 'high' : qualityScore > 40 ? 'medium' : 'low';
    
    return {
      data_quality_score: qualityScore,
      completeness_score: qualityScore,
      confidence_level: confidenceLevel,
      data_sources_available: [
        ...(profileData.profile ? ['profile'] : []),
        ...(profileData.keyPersonnel?.length > 0 ? ['personnel'] : []),
        ...(profileData.pastPerformance?.length > 0 ? ['past_performance'] : []),
        ...(documentsWithText.length > 0 ? ['documents'] : []),
        ...(profileData.capabilities?.length > 0 ? ['capabilities'] : [])
      ],
      documents_count: documentsWithText.length,
      total_document_text_length: totalTextLength
    };
  }

  // Generate AI instructions with document content
  private static generateCleanAIInstructions(
    intelligentContext: IntelligentProposalContext,
    contractData: any
  ): string {
    console.log('📝 Generating clean AI instructions for proposal generation...');

    const profile = intelligentContext.profile_data || {};

    // Helper to format address
    const formatAddress = (addr: any): string => {
      if (!addr) return 'Not specified';
      if (typeof addr === 'string') return addr;
      const parts = [
        addr.street || addr.street1 || '',
        addr.street2 || '',
        addr.city || '',
        addr.state || '',
        addr.zip || addr.zipCode || ''
      ].filter(Boolean);
      return parts.length > 0 ? parts.join(', ') : 'Not specified';
    };

    return `# COMPREHENSIVE COMPANY INTELLIGENCE FOR PROPOSAL GENERATION

## COMPANY PROFILE DATA:
Company Name: ${profile.company_name || 'Professional Services Company'}
Company Description: ${profile.company_description || profile.value_proposition || 'Not specified'}
Business Address: ${formatAddress(profile.business_address)}
Phone: ${profile.phone || profile.company_phone || 'Not specified'}
Email: ${profile.email || profile.company_email || 'Not specified'}
Website: ${profile.website || profile.website_url || 'Not specified'}
LinkedIn: ${profile.linkedin_url || 'Not specified'}
Twitter: ${profile.twitter_url || 'Not specified'}
Facebook: ${profile.facebook_url || 'Not specified'}

## GOVERNMENT CONTRACTING CREDENTIALS:
NAICS Codes: ${Array.isArray(profile.naics_codes) && profile.naics_codes.length > 0 ?
  profile.naics_codes.join(', ') : 'Not specified'}
DUNS Number: ${profile.duns_number || 'Not specified'}
UEI Number: ${profile.uei_number || 'Not specified'}
CAGE Code: ${profile.cage_code || 'Not specified'}
SAM Status: ${profile.sam_status || 'Not specified'}
Certifications: ${Array.isArray(profile.certifications) ? profile.certifications.join(', ') :
  Array.isArray(profile.certifications_held) ? profile.certifications_held.join(', ') : 'Not specified'}
Set-Aside Qualifications: ${Array.isArray(profile.qualified_set_asides) ?
  profile.qualified_set_asides.join(', ') : 'Not specified'}
Security Clearance: ${profile.security_clearance || profile.security_clearance_level || 'Not specified'}
Facility Clearance: ${profile.facility_clearance_level || 'Not specified'}

## COMPANY CAPABILITIES:
Core Competencies: ${Array.isArray(profile.core_competencies) ?
  profile.core_competencies.join(', ') :
  profile.core_competencies || 'Not specified'}
Industries Served: ${Array.isArray(profile.industries_served) ?
  profile.industries_served.join(', ') :
  profile.industries_served || 'Not specified'}
Key Technologies: ${Array.isArray(profile.key_technologies) ?
  profile.key_technologies.join(', ') : 'Not specified'}
Service Offerings: ${Array.isArray(profile.service_offerings) ?
  profile.service_offerings.join(', ') : 'Not specified'}
Years in Business: ${profile.years_in_business || profile.year_established ?
  (new Date().getFullYear() - (profile.year_established || 0)) : 'Not specified'}
Employee Count: ${profile.employee_count || profile.total_employees || 'Not specified'}
Annual Revenue: ${profile.annual_revenue ? '$' + profile.annual_revenue.toLocaleString() : 'Not specified'}

## PERSONNEL DATA:
${intelligentContext.manual_personnel.length > 0 ?
  intelligentContext.manual_personnel.map(person => {
    const details = [
      `Name: ${person.name || person.full_name || 'Unknown'}`,
      `Title: ${person.title || 'Not specified'}`,
      `Experience: ${person.years_experience || 'N/A'} years`,
      person.security_clearance && person.security_clearance !== 'none' ? `Clearance: ${person.security_clearance}` : null,
      person.linkedin_url ? `LinkedIn: ${person.linkedin_url}` : null,
      person.certifications?.length > 0 ? `Certifications: ${person.certifications.join(', ')}` : null,
      person.skills_expertise?.length > 0 ? `Skills: ${person.skills_expertise.slice(0, 5).join(', ')}` : null,
      person.professional_summary ? `Summary: ${person.professional_summary.slice(0, 200)}` : null,
      person.military_service ? `VETERAN - ${person.military_service.branch} (${person.military_service.rank || 'Rank not specified'}, ${person.military_service.years_of_service || person.military_service.yearsOfService || 'N/A'} years service)` : null
    ].filter(Boolean);
    return `### ${person.name || person.full_name}\n${details.join('\n')}`;
  }).join('\n\n') :
  'No personnel data available'}

## VETERAN STATUS SUMMARY:
${(() => {
  const veterans = intelligentContext.manual_personnel.filter(p => p.military_service);
  if (veterans.length === 0) return 'No veterans identified in personnel data.';
  return `Total Veterans: ${veterans.length}\n${veterans.map(v =>
    `- ${v.name || v.full_name}: ${v.military_service?.branch} - ${v.military_service?.rank || 'Rank not specified'}`
  ).join('\n')}`;
})()}

## PAST PERFORMANCE DATA:
${intelligentContext.manual_past_performance.length > 0 ? 
  intelligentContext.manual_past_performance.map(project => 
    `- ${project.project_name}: ${project.description} (${project.contract_value || 'N/A'} value)`
  ).join('\n') : 
  'No past performance data available'}

## CAPABILITIES DATA:
${intelligentContext.manual_capabilities.length > 0 ? 
  intelligentContext.manual_capabilities.map(cap => 
    `- ${cap.category}: ${cap.description}`
  ).join('\n') : 
  'No capabilities data available'}

## DOCUMENT CONTENT FOR ANALYSIS:
${intelligentContext.raw_document_content}

## CONTRACT REQUIREMENTS:
Title: ${contractData.title}
Agency: ${contractData.agency}
NAICS: ${contractData.naicsCode}
Description: ${contractData.description || 'Not specified'}

## INSTRUCTIONS:
Use all the above information to generate a comprehensive, detailed proposal that demonstrates the company's capabilities and alignment with the contract requirements. Extract specific personnel, projects, and capabilities from the document content to create a compelling proposal.`;
  }

  // Generate data quality report
  private static generateCleanDataQualityReport(
    intelligentContext: IntelligentProposalContext,
    profileData: any
  ): any {
    return {
      overall_assessment: intelligentContext.generation_context.confidence_level,
      data_completeness: {
        personnel: intelligentContext.manual_personnel.length,
        past_performance: intelligentContext.manual_past_performance.length,
        documents: intelligentContext.generation_context.documents_count,
        capabilities: intelligentContext.manual_capabilities.length,
        document_text_length: intelligentContext.generation_context.total_document_text_length,
        pricing_components: profileData.pricingData?.length || 0
      },
      intelligence_summary: {
        documents_with_content: intelligentContext.generation_context.documents_count,
        manual_data_entries: intelligentContext.manual_personnel.length + 
                           intelligentContext.manual_past_performance.length + 
                           intelligentContext.manual_capabilities.length,
        data_sources_available: intelligentContext.generation_context.data_sources_available,
        ai_analysis_ready: intelligentContext.raw_document_content.length > 100,
        ai_personnel_found: 0, // Will be populated by AI analysis
        technical_capabilities_found: intelligentContext.manual_capabilities.length
      },
      recommendations: [
        ...(intelligentContext.generation_context.documents_count === 0 ? 
          ['Upload company documents for enhanced analysis'] : []),
        ...(intelligentContext.manual_personnel.length === 0 ? 
          ['Add key personnel information'] : []),
        ...(intelligentContext.manual_past_performance.length === 0 ? 
          ['Add past performance projects'] : []),
        ...(intelligentContext.generation_context.data_quality_score < 50 ? 
          ['Complete additional profile sections for better analysis'] : [])
      ],
      confidence_indicators: {
        high_quality_data_sources: intelligentContext.generation_context.data_sources_available,
        data_completeness_score: intelligentContext.generation_context.completeness_score,
        intelligence_extraction_possible: intelligentContext.raw_document_content.length > 100
      }
    };
  }
}